// src/pages/motolivraison/commercants.page.jsx
//
// Liste des inscriptions commerçant (User.commercantProfile) avec actions admin.
// À ne pas confondre avec commerces.page.jsx qui gère les boutiques (collection Commerce).

import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Modal, message, Button, Card, Row, Col, Tag, Tooltip, Space,
  Breadcrumb, Statistic, Avatar, Select, Dropdown, DatePicker
} from 'antd';
import {
  CheckCircleOutlined, StopOutlined, PlayCircleOutlined,
  ShopOutlined, RightOutlined, DownOutlined, ClockCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { motolivraisonService } from '../../common/services';

const Commercants = () => {
  const [commercants, setCommercants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', dates: null });

  const stats = {
    total: commercants.length,
    pending: commercants.filter(c => c.commercantProfile?.status === 'pending').length,
    approved: commercants.filter(c => c.commercantProfile?.status === 'approved').length,
    suspended: commercants.filter(c => c.commercantProfile?.status === 'suspended').length,
  };

  const fetchCommercants = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.listCommercants({
        status: filters.status || undefined,
        dateDebut: filters.dates?.[0] ? filters.dates[0].format('YYYY-MM-DD') : undefined,
        dateFin: filters.dates?.[1] ? filters.dates[1].format('YYYY-MM-DD') : undefined,
      });
      setCommercants(data?.commercants || []);
    } catch (e) {
      message.error('Erreur lors du chargement des commerçants');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCommercants();
  }, [fetchCommercants]);

  const handleAction = async (action, commercant) => {
    Modal.confirm({
      title: `${action === 'validate' ? 'Approuver' : action === 'suspend' ? 'Suspendre' : 'Réactiver'} ce commerçant ?`,
      content: `${commercant.firstName} ${commercant.lastName} (${commercant.phone})`,
      onOk: async () => {
        try {
          if (action === 'validate') await motolivraisonService.validateCommercant(commercant._id);
          else if (action === 'suspend') await motolivraisonService.suspendCommercant(commercant._id);
          else if (action === 'reactivate') await motolivraisonService.reactivateCommercant(commercant._id);
          message.success('Action effectuée');
          fetchCommercants();
        } catch (e) {
          message.error(e?.message || 'Erreur');
        }
      },
    });
  };

  const statusColor = (s) => ({ pending: 'orange', approved: 'green', suspended: 'red' })[s] || 'default';
  const statusLabel = (s) => ({ pending: 'En attente', approved: 'Approuvé', suspended: 'Suspendu' })[s] || s || '—';

  const columns = [
    {
      title: 'Commerçant',
      key: 'commercant',
      render: (_, r) => (
        <Space>
          <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#fa8c16' }} />
          <div>
            <div style={{ fontWeight: 600 }}>{r.firstName} {r.lastName}</div>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.phone}</div>
            {r.email && <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.email}</div>}
          </div>
        </Space>
      ),
    },
    {
      title: 'Date inscription',
      key: 'date',
      width: 160,
      render: (_, r) => {
        const d = r.commercantProfile?.registeredAt;
        if (!d) return '—';
        return new Date(d).toLocaleDateString('fr-FR', {
          day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
      },
    },
    {
      title: 'Statut',
      key: 'status',
      width: 130,
      render: (_, r) => {
        const s = r.commercantProfile?.status;
        return <Tag color={statusColor(s)}>{statusLabel(s)}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      align: 'center',
      render: (_, r) => {
        const items = [];
        const status = r.commercantProfile?.status;
        if (status === 'pending') {
          items.push({
            key: 'validate',
            icon: <CheckCircleOutlined style={{ color: 'green' }} />,
            label: 'Approuver',
            onClick: () => handleAction('validate', r),
          });
        }
        if (status === 'approved') {
          items.push({
            key: 'suspend',
            icon: <StopOutlined style={{ color: 'red' }} />,
            label: 'Suspendre',
            onClick: () => handleAction('suspend', r),
          });
        }
        if (status === 'suspended') {
          items.push({
            key: 'reactivate',
            icon: <PlayCircleOutlined style={{ color: 'green' }} />,
            label: 'Réactiver',
            onClick: () => handleAction('reactivate', r),
          });
        }
        if (items.length === 0) return null;
        return (
          <Dropdown menu={{ items }}>
            <Button size="small">Actions <DownOutlined /></Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item>Commerçants</Breadcrumb.Item>
        </Breadcrumb>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={6}><Card><Statistic title="Total" value={stats.total} prefix={<ShopOutlined />} /></Card></Col>
          <Col xs={6}><Card><Statistic title="En attente" value={stats.pending} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
          <Col xs={6}><Card><Statistic title="Approuvés" value={stats.approved} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} /></Card></Col>
          <Col xs={6}><Card><Statistic title="Suspendus" value={stats.suspended} prefix={<StopOutlined />} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        </Row>

        <Card title="Commerçants MotoLivraison">
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8}>
              <Select
                placeholder="Filtrer par statut"
                allowClear
                style={{ width: '100%' }}
                value={filters.status || undefined}
                onChange={(v) => setFilters({ ...filters, status: v || '' })}
                options={[
                  { value: 'pending', label: 'En attente' },
                  { value: 'approved', label: 'Approuvés' },
                  { value: 'suspended', label: 'Suspendus' },
                ]}
              />
            </Col>
            <Col xs={24} sm={10}>
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={['Inscrit du', 'au']}
                value={filters.dates}
                onChange={(v) => setFilters({ ...filters, dates: v })}
              />
            </Col>
          </Row>
          <Table
            columns={columns}
            dataSource={commercants.map(c => ({ ...c, key: c._id }))}
            loading={loading}
            pagination={{ pageSize: 20 }}
            scroll={{ x: 700 }}
          />
        </Card>
      </div>
    </div>
  );
};

export default Commercants;
